// M5.8: the small coin balance chip in the AppShell header.
// Shows the running total + a "?" link to the coin history modal.
// Refreshes on window focus and every 30s while the tab is
// visible (so newly-earned coins show up without a manual
// reload). On click, opens a modal with the recent transaction
// history and a CTA to the team rewards page.
//
// Why a header chip vs a dashboard panel? Per ADR-0015 §7,
// both were considered; the header chip wins because the
// "what's my balance?" question comes up between dashboards
// (e.g. when the user is in the admin section and earns
// coins via a trigger). The dashboard panel is a separate
// concern (the TeamRewardCard list) and shows the spend
// direction rather than the running total.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCoinBalance, listCoinTransactions } from './api';
import type { CoinTransaction } from './types';
import type { CurrentUser } from '../../lib/types';
import { TransferCoinsModal } from './TransferCoinsModal';

export function CoinBalanceChip() {
  const [open, setOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const balance = useQuery({
    queryKey: ['me-coins'],
    queryFn: getCoinBalance,
    // 30s polling keeps the chip fresh without manual reload.
    // TanStack Query dedups within the stale window so this
    // only fires when no other component has the same key.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full bg-bg-elevated px-3 py-1 text-xs text-ink hover:bg-bg-elevated/80"
        aria-label={`Coin balance: ${balance.data ?? 0}`}
        title="View coin history"
      >
        <span aria-hidden="true" className="mr-1">🪙</span>
        <span>{balance.data ?? 0}</span>
        <span className="sr-only"> coins</span>
      </button>
      {open ? createPortal(
        <CoinHistoryModal
          onClose={() => setOpen(false)}
          onSend={() => {
            setOpen(false);
            setTransferOpen(true);
          }}
        />,
        document.body,
      ) : null}
      {transferOpen ? createPortal(
        <TransferCoinsModalHost
          balance={balance.data ?? 0}
          onClose={() => setTransferOpen(false)}
        />,
        document.body,
      ) : null}
    </>
  );
}

// Reads the cached ['me'] data without subscribing a
// new useQuery observer. AppShell already loaded this
// on page mount, so the cache is populated. Two
// observers on the same key was causing the AppShell's
// `me` query to flip into isPending during a refetch
// (driven by the chip's competing observer), which
// rendered the AuthLoadingCard and emptied the
// header. Reading the cache directly via getQueryData
// avoids the observer subscription entirely.
function TransferCoinsModalHost({ balance, onClose }: { balance: number; onClose: () => void }) {
  const qc = useQueryClient();
  const me = qc.getQueryData<{ user: CurrentUser }>(['me']);
  if (!me?.user) {
    // The ['me'] cache hasn't populated yet. Shouldn't
    // happen in practice since AppShell loads it on
    // mount and the user can only click "Send" from a
    // chip that's inside the rendered shell. If it does
    // happen, fall through to the modal-less state
    // (the chip's onClose isn't reachable without a
    // click, so the outer setTransferOpen(true) call
    // won't be undone).
    return null;
  }
  return <TransferCoinsModal me={me.user} balance={balance} onClose={onClose} />;
}

function CoinHistoryModal({ onClose, onSend }: { onClose: () => void; onSend: () => void }) {
  const txs = useQuery({
    queryKey: ['me-coin-transactions'],
    queryFn: () => listCoinTransactions(20),
  });
  // ESC closes the modal. Standard dialog UX; matches
  // the GrantXpCoinsModal pattern (which is the
  // project-wide convention for modals). Without
  // this, keyboard-only users have no way to leave.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coin-history-title"
      // Click on the backdrop closes. The inner panel
      // uses stopPropagation so clicks ON the modal
      // body don't bubble up and close it. M5.10 fix:
      // the previous version had stopPropagation on
      // the outer div, which made the backdrop
      // unclickable and forced the user to use the X
      // button — a footgun for mouse users who'd
      // naturally click outside the modal to dismiss.
      onClick={onClose}
    >
      <div
        // M5.11: split the inner panel into a fixed
        // header (title + close) and a scrollable
        // body (transactions). The previous layout
        // put everything in a single scrollable
        // `max-h-[80vh] overflow-y-auto` div, which
        // meant the title scrolled OFF the top when
        // the user scrolled down to see more
        // transactions. With 20 transactions the
        // list regularly exceeds 80vh, so the title
        // was clipped behind the top of the viewport
        // for most users most of the time. The fix:
        // flex column with a non-shrinking header
        // and a `flex-1 overflow-y-auto` body, so the
        // header always sits at the top of the
        // modal panel and the body scrolls
        // independently.
        className="card flex max-h-[80vh] w-full max-w-lg flex-col p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-white/5 p-4">
          <h2 id="coin-history-title" className="text-lg font-semibold">
            Coin history
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSend}
              className="btn-primary text-xs"
            >
              Send coins
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
        {txs.isPending ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : txs.isError ? (
          <p className="text-sm text-err">Failed to load history.</p>
        ) : txs.data.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No transactions yet. Earn coins by shipping work, giving kudos, or
            completing quests.
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {txs.data.map((t) => (
              <li key={t.id} className="flex items-center justify-between py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    {describeTx(t)}
                  </div>
                  <div className="text-xs text-ink-muted">{formatTime(t.created_at)}</div>
                </div>
                <div className={amountClass(t.amount)}>
                  {t.amount > 0 ? '+' : ''}{t.amount}
                </div>
              </li>
            ))}
          </ul>
        )}
        </div>
      </div>
    </div>
  );
}

function describeTx(t: CoinTransaction): string {
  // Spend to a team reward: render the reward's name.
  if (t.kind === 'spend' && t.reward_name) {
    return `Contributed to ${t.reward_name}`;
  }
  // Refund (admin voided the contribution or claim).
  if (t.kind === 'refund') {
    return `Refund (admin void)`;
  }
  // Earn from a team reward (M5.8: player claims).
  if (t.kind === 'earn' && t.reward_name) {
    return `Earned from ${t.reward_name}`;
  }
  // M5.11: earn from a scoring rule. Show the rule's
  // free-form description (admin-written label) so
  // the player sees 'You closed a bug!' instead of
  // 'rule rule_ms2u382f3dc6'. The 'You ' prefix
  // makes the rule read as the player-facing action
  // (the rule is a description of what the player
  // did, so "You <description>" reads naturally).
  // If the rule has no description we fall through
  // to the legacy fallback below. The 'You ' prefix
  // is capitalised; no trailing punctuation since
  // descriptions already end with or without a
  // period (admin's choice).
  if (t.kind === 'earn' && t.rule_description) {
    return `You ${t.rule_description}`;
  }
  // M5.11: earn from the status_transition_xp matrix
  // (rule_id is null because the matrix row id is
  // stored in matrix_id, not rule_id). The reason
  // for matrix-driven earns is set to the matrix row
  // id in ingest-pipeline.ts; if a future commit
  // joins matrix_id to status_transition_xp, this
  // fallback will pick up the to_status / from_status
  // pair as a more human label. For now, fall
  // through to the reason/kind text.
  return t.reason ?? t.kind;
}

function amountClass(amount: number): string {
  return `ml-3 font-mono ${amount > 0 ? 'text-accent-success' : 'text-ink-muted'}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  // Compact "2 hours ago" formatting. Falls back to the
  // absolute date if the diff is > 7 days.
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString();
}
